import { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from 'common/ui/Icon';
import Tooltip from 'common/ui/Tooltip';

type useCopyableContentProps = {
  value: string;
};

export const useCopyableContent = ({
  value,
}: useCopyableContentProps): {
  copyOnClick: (event: React.MouseEvent<Element>) => void;
  CopyTooltip: (arg1: { children: React.ReactElement }) => React.ReactElement;
  copied: boolean;
} => {
  const [copied, setCopied] = useState(false);

  const copyOnClick = useCallback(
    (event: React.MouseEvent<Element>): void => {
      event.stopPropagation();
      navigator.clipboard.writeText(value);
      setCopied(true);
    },
    [value]
  );

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const tooltipText = useMemo(
    () => (
      <span css="display: flex; align-items: center; gap: 4px;">
        <Icon name={copied ? 'check' : 'file_copy'} css="height: 12px; width: 12px;" />
        {copied ? 'Copied' : 'Copy to clipboard'}
      </span>
    ),
    [copied]
  );

  const CopyTooltip = ({ children }: { children: React.ReactElement }) => (
    <Tooltip content={tooltipText} hideOnClick={false} centerContent>
      {children}
    </Tooltip>
  );

  return { CopyTooltip, copyOnClick, copied };
};
